
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useStore } from '@/store';
import { CampaignAutomationStatusEnum } from '@/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import { showToastSuccess, showToastError } from '@/helpers';
import {
  getChannelIcon,
  getChannelTagStyleVars,
  getStatusIcon,
  getStatusStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmMetricDisplay from '@/components/table-rest-api/SpmMetricDisplay.vue';
import SpmHelpIcon from '@/components/table-rest-api/SpmHelpIcon.vue';
import {
  formatPercentage as formatPercentageUtil,
  formatCurrency,
  formatInteger as formatIntegerUtil,
} from '@/components/table-rest-api/formatters';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  SpmActiveSort,
  ExportPayload,
  SpmTableAPIExposed,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { isDemoModeActive, demoCampaigns } from '@/composables/DemoMode';
import ImportModal from '@/components/import/ImportModal.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import CampaignAutomationSelectorV2 from '@/components/automated-scenarios/CampaignAutomationSelectorV2.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import {
  ActivateCampaignAction,
  DeactivateCampaignAction,
  DuplicateCampaignAction,
  DeleteCampaignAction,
  useAutomationCampaignsActions,
} from './AutomationCampaignsActions';
import type { AutomationCampaignData } from './AutomationCampaignsActions/types';
import { PublishToGalleryDialog } from './BulkCampaignsActions';
import { updateAutomationTrackSales } from '@/composables/flows/useFlowApi';

export default defineComponent({
  name: 'CampaignsAutomationList',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmBadge,
    SpmDateDisplay,
    SpmMetricDisplay,
    SpmHelpIcon,
    ImportModal,
    SpmButton,
    SpmEmptyState,
    CampaignAutomationSelectorV2,
    IntegrationOwnerBadge,
    ActivateCampaignAction,
    DeactivateCampaignAction,
    DuplicateCampaignAction,
    DeleteCampaignAction,
    PublishToGalleryDialog,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const tableRef = ref<SpmTableAPIExposed | null>(null);
    const showImportModal = ref(false);
    const showCreateModal = ref(false);

    // Watch flow editor visibility to reload table when editor closes
    const isFlowEditorVisible = computed(() => store.getters['flowEditor/isEditorVisible']);
    watch(isFlowEditorVisible, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Watch import modal visibility to reload table when editor closes
    watch(showImportModal, (newValue, oldValue) => {
      // Reload table when editor closes (visible -> hidden)
      if (oldValue === true && newValue === false && tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    });

    // Action component refs
    const activateRef = ref(null);
    const deactivateRef = ref(null);
    const duplicateRef = ref(null);
    const deleteRef = ref(null);
    const publishGalleryRef = ref<{ open: (list: { id: number; name: string }[]) => void; done: () => void } | null>(null);

    // Hidden statuses (not shown in the list)
    const hiddenStatuses: CampaignAutomationStatusEnum[] = [
      CampaignAutomationStatusEnum.ARCHIVED,
      CampaignAutomationStatusEnum.DELETED,
    ];

    // Status options for filter (all except hidden statuses)
    const statusOptions = computed(() => (Object.values(CampaignAutomationStatusEnum) as CampaignAutomationStatusEnum[])
      .filter((status) => !hiddenStatuses.includes(status))
      .map((status) => ({
        label: t(`campaignsAutomationList.status.${status}`),
        value: status,
      })));

    // API call configuration.
    // `withStats=false` : le listing revient sans aucune métrique (rapide) ;
    // les colonnes `lazy` sont remplies ensuite via `loadCampaignsStats`.
    const apiCall = ref<ApiCallConfig>({
      type_api: NestApiType.CLIENT_API,
      path: '/campaigns/automations?withStats=false',
      secure: true,
    });

    /**
     * Stats différées des colonnes lazy : un appel scopé sur les ids du lot,
     * réponse indexée par clé de ligne pour le merge dans le tableau.
     */
    const loadCampaignsStats = async (
      rows: Record<string, unknown>[],
    ): Promise<Record<string, Record<string, unknown>> | null> => {
      const ids = rows.map((row) => row.id).filter(Boolean);
      if (!ids.length) {
        return null;
      }

      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/campaigns/automations/stats',
        {},
        '',
        undefined,
        true,
        { ids: ids.join(',') },
      );

      if (!response?.data || !Array.isArray(response.data)) {
        return null;
      }

      return Object.fromEntries(
        response.data.map(
          (stats: Record<string, unknown>): [string, Record<string, unknown>] => [String(stats.id), stats],
        ),
      );
    };

    // Persistent filters - exclude hidden statuses + demo mode filter
    const persistentFilters = computed<SpmApiFilter[]>(() => {
      const filters = hiddenStatuses.map((status) => ({
        field: 'status',
        operator: SpmFilterOperator.NOT_EQUALS,
        values: status,
      }));

      // In demo mode, only show selected campaigns (or none if empty)
      if (isDemoModeActive.value) {
        const ids = demoCampaigns.automationIds;
        filters.push({
          field: 'id',
          operator: SpmFilterOperator.IN,
          values: (ids.length > 0 ? ids.join(',') : '0') as any,
        });
      }

      return filters;
    });

    // Initial sort - most recent first
    const initialSort: SpmActiveSort = {
      field: 'date_modification',
      order: 'desc',
    };

    // Columns configuration
    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'name',
        header: t('campaignsAutomationList.columns.name'),
        maxWidth: '35%',
        fields: [
          {
            name: 'name',
            label: t('campaignsAutomationList.columns.name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          {
            name: 'date_creation',
            label: t('campaignsAutomationList.columns.createdAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          {
            name: 'date_modification',
            label: t('campaignsAutomationList.columns.modifiedAt'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'dateRange' },
          },
          'primary_channel',
          'channels',
        ],
      },
      {
        id: 'status',
        header: t('campaignsAutomationList.columns.status'),
        fields: [
          {
            name: 'status',
            label: t('campaignsAutomationList.columns.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: statusOptions.value,
            },
          },
        ],
      },
      {
        id: 'performance',
        lazy: true,
        header: t('campaignsAutomationList.columns.performance'),
        fields: [
          {
            name: 'open_rate',
            label: t('campaignsAutomationList.metrics.openRate'),
            sortable: true,
          },
          {
            name: 'open_rate_total',
            label: t('campaigns.common.metrics.openRateTotal'),
            sortable: true,
          },
          {
            name: 'click_rate',
            label: t('campaignsAutomationList.metrics.clickRate'),
            sortable: true,
          },
          'deliveries',
          'opens',
          'open_unique',
          'clicks',
          'click_unique',
        ],
      },
      {
        id: 'conversions',
        lazy: true,
        header: t('campaignsAutomationList.columns.conversions'),
        fields: [
          {
            name: 'sales_amount',
            label: t('campaignsAutomationList.metrics.salesAmount'),
            sortable: true,
          },
          {
            name: 'conversion_rate',
            label: t('campaignsAutomationList.metrics.conversionRate'),
            sortable: true,
          },
          'sales',
          'bounces',
          'spam',
          'unsubscribes',
          'revenue_per_send',
        ],
      },
    ]);

    // Use the composable for actions
    const { rowActions, groupedActions } = useAutomationCampaignsActions({
      activateRef,
      deactivateRef,
      duplicateRef,
      deleteRef,
      onToggleTrackSales: async (campaign, trackSales) => {
        try {
          setLoading(true);
          await updateAutomationTrackSales(campaign.id, trackSales);
          await showToastSuccess(
            t(
              trackSales
                ? 'campaignsAutomationList.actions.trackSalesSuccess'
                : 'campaignsAutomationList.actions.untrackSalesSuccess',
            ),
          );
          reloadTableData();
        } catch (error) {
          console.error('Toggle track_sales error:', error);
          await showToastError(t('campaignsAutomationList.actions.trackSalesError'));
        } finally {
          setLoading(false);
        }
      },
      onPublishToGallery: async (selection) => {
        if (selection.selectionCount === 0) return;

        try {
          setLoading(true);
          // Build query params based on selection mode
          const params: Record<string, any> = {
            fields: ['id', 'name'],
            limit: selection.selectionCount,
          };

          if (selection.selectAllMode) {
            if (selection.excludedRows.length > 0) {
              params.filters = [{ field: 'id', operator: 'not_in', values: selection.excludedRows.join(',') }];
            }
          } else {
            params.filters = [{ field: 'id', operator: 'in', values: selection.selectedRows.join(',') }];
          }

          const response = await nestCall(
            'get',
            NestApiType.CLIENT_API,
            '/campaigns/automations',
            {},
            '',
            undefined,
            true,
            params,
          );
          const campaignList = (response?.data || []).map((c: any) => ({ id: Number(c.id), name: c.name }));
          if (campaignList.length > 0 && publishGalleryRef.value?.open) {
            publishGalleryRef.value.open(campaignList);
          }
        } catch (error) {
          console.error('Publish to gallery error:', error);
          await showToastError(t('campaignsAutomationList.groupedActions.publishToGalleryError'));
        } finally {
          setLoading(false);
        }
      },
    });

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (tableRef.value?.unselectAll) {
        tableRef.value.unselectAll();
      }
    };

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (tableRef.value?.reloadData) {
        tableRef.value.reloadData();
      }
    };

    // Handler for action confirmations (table reload)
    const onActionConfirm = () => {
      resetTableSelection();
      reloadTableData();
    };

    // Handler for publish to gallery dialog confirmation
    const onPublishToGalleryConfirm = async (payload: { campaignIds: number[]; campaignIdsWithoutTemplates: number[] }) => {
      try {
        setLoading(true);
        const response = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/data/publish-to-gallery',
          {},
          '',
          {
            shopId: UserState.activeShop?.id,
            campaignIds: payload.campaignIds,
            campaignIdsWithoutTemplates: payload.campaignIdsWithoutTemplates,
            type: 'campaign_automation',
          },
          true,
        );
        if (response?.success) {
          await showToastSuccess(t('campaignsAutomationList.groupedActions.publishToGallerySuccess'));
        } else {
          await showToastError(response?.message || t('campaignsAutomationList.groupedActions.publishToGalleryError'));
        }
      } catch (error) {
        console.error('Publish to gallery error:', error);
        await showToastError(t('campaignsAutomationList.groupedActions.publishToGalleryError'));
      } finally {
        if (publishGalleryRef.value?.done) {
          publishGalleryRef.value.done();
        }
        resetTableSelection();
        setLoading(false);
      }
    };

    const setLoading = async (loading: boolean) => {
      if (tableRef.value) {
        await tableRef.value.setLoading(loading);
      }
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );
        if (response && !response.error) {
          await showToastSuccess(t('campaignsAutomationList.export.success'));
        } else {
          await showToastSuccess(t('campaignsAutomationList.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        resetTableSelection();
        reloadTableData();
        setLoading(false);
      }
    };

    // Formatting functions - using shared formatters
    const formatPercentage = (value: number | null | undefined): string => formatPercentageUtil(value, false);

    const formatAmount = (value: number | null | undefined): string => formatCurrency(value, 'EUR');

    const formatInteger = (value: number | null | undefined): string => formatIntegerUtil(value);

    const formatChannel = (value: string | null): string => {
      if (!value) return '-';
      const key = value.toLowerCase();
      const translationKey = `campaignsAutomationList.channels.${key}`;
      const translated = t(translationKey);
      return translated === translationKey ? key : translated;
    };

    const extractChannelTags = (row: AutomationCampaignData): string[] => {
      const uniqueChannels = new Set<string>();

      if (Array.isArray(row?.channels)) {
        row.channels.forEach((channel) => {
          if (channel?.channel) {
            uniqueChannels.add(channel.channel);
          }
        });
      }

      if (row?.primary_channel) {
        uniqueChannels.add(row.primary_channel);
      }

      return Array.from(uniqueChannels);
    };

    // Style functions - convert CSS vars format to SpmBadge format
    const getStatusStyle = (status: string | null | undefined) => {
      const vars = getStatusStyleVars(status);
      return {
        bg: vars['--status-bg'] || 'var(--surface-200)',
        color: vars['--status-color'] || 'var(--text-color)',
        borderColor: vars['--status-border'],
      };
    };
    const trackSalesBadgeStyle = {
      bg: 'rgba(34, 197, 94, 0.12)',
      color: '#16a34a',
      borderColor: 'rgba(34, 197, 94, 0.3)',
    };

    const getChannelTagStyle = (channel: string | null | undefined) => {
      const vars = getChannelTagStyleVars(channel);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    return {
      t,
      tableRef,
      apiCall,
      loadCampaignsStats,
      columns,
      persistentFilters,
      initialSort,
      rowActions,
      groupedActions,
      showImportModal,
      showCreateModal,
      // Action component refs
      activateRef,
      deactivateRef,
      duplicateRef,
      deleteRef,
      publishGalleryRef,
      onActionConfirm,
      onPublishToGalleryConfirm,
      // Formatting functions
      formatPercentage,
      formatAmount,
      formatInteger,
      formatChannel,
      extractChannelTags,
      // Style functions
      getStatusStyle,
      getStatusIcon,
      getChannelTagStyle,
      getChannelIcon,
      trackSalesBadgeStyle,
      handleExport,
    };
  },
});
